import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * Unit B — DNS & email management on a managed domain. Every mutation is
 * expressed as a single `DnsAction`, run through preview (compute the plan,
 * touch nothing) or apply (execute + log). The server always recomputes the
 * plan from the live zone; the client never sends a plan to trust.
 */
/** Route params: the fleet, and the managed zone (harbour.co.uk). */
export const domainDnsParamSchema = z.object({
    fleetId: idSchema,
    domain: z.string().trim().toLowerCase().min(4).max(253),
});
export const emailPresetIdSchema = z.enum(['google-workspace', 'microsoft-365', 'other']);
export const editableRecordTypeSchema = z.enum(['A', 'AAAA', 'CNAME', 'TXT', 'MX']);
/** A record as the custom editor submits it — value is the type-specific
 *  payload (address / cname / TXT value / MX exchange). */
export const customRecordInputSchema = z
    .object({
    type: editableRecordTypeSchema,
    name: z.string().trim().max(253),
    value: z.string().trim().min(1).max(2048),
    ttl: z.number().int().min(60).max(604_800).optional(),
    /** MX only. */
    preference: z.number().int().min(0).max(65_535).optional(),
})
    .strict();
/** The (type, name, value) triple the registrar matches a deletion on. */
export const recordRefSchema = z
    .object({
    type: z.string().trim().min(1).max(10),
    name: z.string().trim().max(253),
    value: z.string().trim().min(1).max(2048),
})
    .strict();
const otherMailSchema = z
    .object({
    mx: z
        .array(z
        .object({
        exchange: z.string().trim().min(1).max(253),
        preference: z.number().int().min(0).max(65_535),
    })
        .strict())
        .min(1)
        .max(10),
    spfInclude: z.string().trim().min(1).max(253).optional(),
})
    .strict();
/** Turn on an email provider (MX + merged SPF [+ autodiscover]). */
export const emailPresetActionSchema = z
    .object({
    kind: z.literal('email_preset'),
    preset: emailPresetIdSchema,
    /** Required iff preset === 'other'. */
    other: otherMailSchema.optional(),
})
    .strict();
/** Add or replace a custom record. `replaces` targets a specific existing
 *  value to remove (a true edit); omit it to add alongside. */
export const customUpsertActionSchema = z
    .object({
    kind: z.literal('custom_upsert'),
    record: customRecordInputSchema,
    replaces: recordRefSchema.optional(),
})
    .strict();
/** Delete a specific custom record. */
export const customDeleteActionSchema = z
    .object({
    kind: z.literal('custom_delete'),
    record: recordRefSchema,
})
    .strict();
export const dnsActionSchema = z.discriminatedUnion('kind', [
    emailPresetActionSchema,
    customUpsertActionSchema,
    customDeleteActionSchema,
]);
/** Preview and apply share the action body. */
export const dnsActionBodySchema = z.object({ action: dnsActionSchema }).strict();
//# sourceMappingURL=domain-dns.js.map